<template>
  <div class="input-wrapper">
    <div
      :class="`input ${disabled ? 'disabled' : ''} ${error ? 'has-error' : ''} ${icon ? 'has-icon' : ''} ${stepper ? 'has-stepper' : ''} ${fixedPlaceholder ? 'has-fixed-placeholder' : ''} ${copy ? 'has-copy' : ''} ${copied ? 'has-copied' : ''} ${tooltip ? 'has-tooltip' : ''} ${resetable ? 'has-reset' : ''}`"
    >
      <span class="step" v-if="stepper" @click="stepDown">-</span>
      <div class="input-inner">
        <i :class="`icon-${icon} input-icon`" v-if="icon" />
        <input
          :type="type === 'password' ? peeking ? 'text' : 'password' : type"
          :disabled="disabled"
          :autocomplete="autocomplete ? 'on' : 'off'"
          :step="step"
          :placeholder="placeholder"
          :size="size"
          :maxlength="maxlength"
          :value="value"
          :id="id"
          :min="min"
          :max="max"
          :readonly="readonly || disabled"
          @input="emit($event.target.value)"
          @blur="$emit('validate', true)"
          @keydown.enter="$emit('enter', true)"
        />
        <div class="right-side">
          <i :class="`icon-${stateIcon} state-icon ${stateIconColor}`" v-if="stateIcon" />
          <span class="loading" v-if="loading"></span>
          <template v-else>
            <div class="icons" v-if="copy || tooltip || type === 'password' || resetable">
              <i class="icon-copy-24 copy-icon" @click="copyString" v-if="copy" />
              <i class="icon-information-24 tooltip-icon" v-if="tooltip">
                <div class="tooltip-text" v-html="tooltipText"></div>
              </i>
              <i :class="`${peeking ? 'icon-eye-24' : 'icon-eye-strike-24'} peek-icon`" @click="peeking = !peeking" v-if="type === 'password'" />
              <div class="d-f ai-c jc-c reset" v-if="resetable" @click="resetfunc">
                <i class="icon-arrow-back" />
                <p>Reset</p>
              </div>
            </div>
            <div class="fixed-placeholder" v-if="fixedPlaceholder">
              <span>{{ fixedPlaceholder }}</span>
            </div>
          </template>
        </div>
      </div>
      <span class="step" v-if="stepper" @click="stepUp">+</span>
    </div>
    <p class="error" v-if="error">{{ error }}</p>
  </div>
</template>

<script>
export default {
  props: {
    type: {
      type: String,
      default: 'text',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    value: {
      // eslint-disable-next-line
      type: String|Number,
      default: '',
    },
    id: {
      // eslint-disable-next-line
      type: String|Number,
      default: '',
    },
    icon: {
      type: String,
      default: '',
    },
    stateIcon: {
      type: String,
      default: '',
    },
    stateIconColor: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    fixedPlaceholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    min: {
      // eslint-disable-next-line
      type: String|Number,
      default: '',
    },
    max: {
      // eslint-disable-next-line
      type: String|Number,
      default: '',
    },
    maxlength: {
      type: Number,
      default: null,
    },
    stepper: {
      type: Boolean,
      default: false,
    },
    step: {
      type: Number,
      default: 0,
    },
    tooltip: {
      type: Boolean,
      default: false,
    },
    tooltipText: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    copy: {
      type: Boolean,
      default: false,
    },
    autocomplete: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
    resetable: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    emit(value) {
      this.$emit('input', `${value}`);
    },
    /**
     * Step number up by {step}
     * @returns {Void}
     */
    stepUp() {
      const { step } = this;
      if (this.max && this.value + step > this.max) {
        return;
      }
      this.emit(this.value + step);
    },
    /**
     * Step number down by {step}
     * @returns {Void}
     */
    stepDown() {
      const { step } = this;
      if (this.min && this.value - step < this.min) {
        return;
      }
      this.emit(this.value - step);
    },
    /**
     * Copy text in input
     * @returns {Void}
     */
    copyString() {
      clearTimeout(this.copyTimer);
      window.Clipboard.copy(this.$el.querySelector('input').value);
      this.copied = true;
      this.copyTimer = setTimeout(() => {
        this.copied = false;
      }, 500);
    },
    resetfunc() {
      this.$emit('reset', true);
    },
  },
  data() {
    return {
      copied: false,
      copyTimer: null,
      peeking: false,
    };
  },
  beforeDestroy() {
    clearTimeout(this.copyTimer);
  },
};
</script>

<style lang="scss" scoped>
.input-wrapper {
  &.small {
    input {
      width: 60px;
    }
  }

  &.center-text {
    input {
      text-align: center;
    }
  }

  &.mono {
    input {
      font: 12px/18px 'Fira Mono', monospace;
    }
  }

  &.thin {
    input {
      padding: 10px;
    }
  }

  &.dots-padding {
    input {
      padding-right: 50px;
    }
  }
}

.input {
  background: #FFFFFF;
  border: 1px solid #D7D9E0;
  box-shadow: inset 0 1px 2px 0 rgba(0,0,0,0.10);
  position: relative;
  width: 100%;

  &.has-error {
    background: #FFF1F0;
    border: 1px solid #FF4848;
    box-shadow: inset 0 1px 2px 0 rgba(0,0,0,0.10);

    input {
      outline-color: var(--color-cta-red);
    }
  }

  &.disabled {
    background-color: #f3f3f3;
  }

  &.has-icon {
    input {
      padding-left: 40px;
    }
  }

  &.has-reset {
    input {
      padding-right: 96px;
    }
  }

  &.has-copy {
    input {
      padding-right: 40px;
    }
  }

  &.has-copied {
    input, input:read-only {
      color: #0060FF;
    }
  }

  &.has-stepper {
    display: flex;
  }

  &.has-fixed-placeholder {
    input {
      padding-right: 15px;
    }
  }
}

.error {
  font-style: italic;
  font-size: 13px;
  line-height: 18px;
  margin-top: 2px;
  color: #FF1717;
}

.input-inner {
  position: relative;
  width: 100%;
}

input {
  color: black;
  font-size: 16px;
  padding: 15px 12px;
  width: 100%;
  background-color: transparent;
  outline-color: var(--color-cta-blue);

  &[type="number"] {
    appearance: none;
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button, &::-webkit-inner-spin-button {
      appearance: none;
      -webkit-appearance: none;
      margin: 0;
    }
  }

  &::placeholder {
    color: #8D92A1;
  }
}

.input-icon {
  color: #4e525e;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  position: absolute;
  font-size: 24px;
}

.copy-icon, .tooltip-icon, .peek-icon, .state-icon {
  color: var(--color-cta-blue);
  font-size: 24px;

  &:hover {
    cursor: pointer;
  }
}

.reset {
  i {
    font-size: 24px;
  }

  p {
    font-weight: 600;
  }

  &:hover {
    cursor: pointer;
  }

  @media (max-width: 500px) {
    p {
      display: none;
    }
  }
}

.state-icon {
  margin-right: 10px;

  &.green {
    color: var(--color-cta-green);
  }
  &.red {
    color: var(--color-cta-red);
  }
}

.tooltip-icon {
  .tooltip-text {
    display: none;
    background-color: white;
    border: 1px solid #B7BDC6;
    border-radius: 5px;
    box-shadow: 2px 2px 0 #CED4DE;
    position: absolute;
    top: calc(100% + 10px);
    transform: translateX(-80%);
    z-index: 2000;
    min-width: 200px;
    padding: 12px;
    max-width: 200px;
    font-size: 12px;
    color: grey;
    font-style: normal;

    &:before, &:after {
      bottom: 100%;
      left: 170px;
      border: solid transparent;
      content: "";
      height: 0;
      width: 0;
      position: absolute;
      pointer-events: none
    }

    &:after {
      border-color: rgba(255, 255, 255, 0);
      border-bottom-color: #ffffff;
      border-width: 10px;
      margin-left: -10px;
    }

    &:before {
      border-color: rgba(183, 189, 198, 0);
      border-bottom-color: #B7BDC6;
      border-width: 11px;
      margin-left: -11px;
    }
  }

  &:hover, &:focus {
    .tooltip-text {
      display: block;
    }
  }
}

.right-side {
  position: absolute;
  right: 1px;
  top: 1px;
  bottom: 1px;
  display: flex;
  justify-content: center;
  align-items: center;

  & > * {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;

    & > * {
      flex: 1;
    }
  }
}

.icons {
  padding: 0 10px;
  border-left: 1px solid #D7D9E0;
  background-color: white;
  color: var(--color-cta-blue);

  & > * {
    &:not(:first-child) {
      margin-left: 4px;
    }
  }
}

.fixed-placeholder {
  position: absolute;
  right: 1px;
  padding: 0 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #9297A2;
}

.step {
  background-color: #F7F8FA;
  border: 1px solid #DCE1EA;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  user-select: none;

  &:hover {
    cursor: pointer;
  }
}

.loading, .loading:after {
  border-radius: 50%;
  width: 18px;
  height: 18px;
}
.loading {
  font-size: 10px;
  text-indent: -9999em;
  border-top: 2px solid rgba(0, 0, 0, 0.2);
  border-right: 2px solid rgba(0, 0, 0, 0.2);
  border-bottom: 2px solid rgba(1, 0, 0, 0.2);
  border-left: 2px solid #ffffff;
  transform: translateZ(0);
  animation: spinner 1.1s infinite linear;
  margin-right: 10px;
}

@keyframes spinner {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
